import { useState, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Plus, Trash2, Type, Mail, Phone, AlignLeft, ChevronDown,
  CheckSquare, Circle, Calendar, Link as LinkIcon, Upload, Hash, EyeOff,
  Settings, Copy as CopyIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';

export type FormFieldType =
  | 'text' | 'email' | 'tel' | 'phone' | 'number' | 'textarea'
  | 'select' | 'checkbox' | 'radio' | 'date' | 'url'
  | 'file' | 'hidden';

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldData {
  id: string; // local id for DnD; not persisted
  name: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  placeholder?: string;
  helper?: string;
  default?: string | number | boolean;
  options?: FormFieldOption[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  width?: 'full' | 'half' | 'third' | 'two-thirds';
  step?: number;
}

interface FormBuilderProps {
  fields: FormFieldData[];
  onChange: (fields: FormFieldData[]) => void;
}

const FIELD_LIBRARY: Array<{
  type: FormFieldType;
  label: string;
  icon: ReactNode;
  defaults: Partial<FormFieldData>;
}> = [
  { type: 'text', label: 'Texto', icon: <Type className="size-4" />, defaults: { label: 'Texto', placeholder: '' } },
  { type: 'email', label: 'Email', icon: <Mail className="size-4" />, defaults: { label: 'Email', placeholder: 'tu@email.com', required: true } },
  { type: 'tel', label: 'Teléfono', icon: <Phone className="size-4" />, defaults: { label: 'Teléfono', placeholder: '+34 ...' } },
  { type: 'textarea', label: 'Texto largo', icon: <AlignLeft className="size-4" />, defaults: { label: 'Mensaje', placeholder: '' } },
  { type: 'select', label: 'Desplegable', icon: <ChevronDown className="size-4" />, defaults: { label: 'Selecciona', options: [{ value: 'a', label: 'Opción A' }, { value: 'b', label: 'Opción B' }] } },
  { type: 'checkbox', label: 'Checkbox', icon: <CheckSquare className="size-4" />, defaults: { label: 'Acepto los términos', required: true } },
  { type: 'radio', label: 'Radio', icon: <Circle className="size-4" />, defaults: { label: 'Opción', options: [{ value: 'a', label: 'Sí' }, { value: 'b', label: 'No' }] } },
  { type: 'date', label: 'Fecha', icon: <Calendar className="size-4" />, defaults: { label: 'Fecha' } },
  { type: 'number', label: 'Número', icon: <Hash className="size-4" />, defaults: { label: 'Número' } },
  { type: 'url', label: 'URL', icon: <LinkIcon className="size-4" />, defaults: { label: 'Web', placeholder: 'https://…' } },
  { type: 'file', label: 'Archivo', icon: <Upload className="size-4" />, defaults: { label: 'Adjunto' } },
  { type: 'hidden', label: 'Oculto', icon: <EyeOff className="size-4" />, defaults: { label: 'Hidden field', name: 'hidden_value' } },
];

const ID = () => Math.random().toString(36).slice(2, 10);

function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'campo';
}

export function FormBuilder({ fields, onChange }: FormBuilderProps) {
  const [selectedId, setSelectedId] = useState<string | null>(fields[0]?.id ?? null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function addField(type: FormFieldType) {
    const tpl = FIELD_LIBRARY.find((t) => t.type === type);
    const baseLabel = (tpl?.defaults.label as string | undefined) ?? 'Campo';
    const baseName = (tpl?.defaults.name as string | undefined) ?? slug(baseLabel);
    const newField: FormFieldData = {
      ...(tpl?.defaults ?? {}),
      id: ID(),
      name: uniqueName(baseName, fields),
      label: baseLabel,
      type,
      width: 'full',
    };
    const next = [...fields, newField];
    onChange(next);
    setSelectedId(newField.id);
  }

  function updateField(id: string, patch: Partial<FormFieldData>) {
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }

  function removeField(id: string) {
    const next = fields.filter((f) => f.id !== id);
    onChange(next);
    if (selectedId === id) setSelectedId(next[0]?.id ?? null);
  }

  function duplicateField(id: string) {
    const idx = fields.findIndex((f) => f.id === id);
    if (idx < 0) return;
    const original = fields[idx];
    if (!original) return;
    const copy: FormFieldData = {
      ...original,
      id: ID(),
      name: uniqueName(original.name + '_copy', fields),
      label: original.label + ' (copia)',
    };
    const next = [...fields.slice(0, idx + 1), copy, ...fields.slice(idx + 1)];
    onChange(next);
    setSelectedId(copy.id);
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(fields, oldIndex, newIndex));
  }

  const selected = fields.find((f) => f.id === selectedId) ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-[200px_1fr_320px]">
      <FieldLibrary onAdd={addField} />

      <Card className="overflow-hidden">
        <div className="border-b border-neutral-200 bg-neutral-50 p-3">
          <h3 className="text-sm font-semibold text-neutral-700">Lienzo del formulario</h3>
          <p className="text-xs text-neutral-500">
            Arrastra para reordenar. Haz clic en un campo para editarlo a la derecha.
          </p>
        </div>
        <div className="space-y-2 p-3">
          {fields.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-neutral-200 p-12 text-center text-sm text-neutral-400">
              Añade tu primer campo desde la izquierda.
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              {fields.map((f) => (
                <SortableFieldRow
                  key={f.id}
                  field={f}
                  isSelected={f.id === selectedId}
                  onSelect={() => setSelectedId(f.id)}
                  onRemove={() => removeField(f.id)}
                  onDuplicate={() => duplicateField(f.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </Card>

      <FieldInspector
        field={selected}
        onChange={(patch) => selected && updateField(selected.id, patch)}
        onRemove={() => selected && removeField(selected.id)}
      />
    </div>
  );
}

function uniqueName(base: string, fields: FormFieldData[]): string {
  const s = slug(base);
  let candidate = s;
  let i = 2;
  while (fields.some((f) => f.name === candidate)) {
    candidate = `${s}_${i}`;
    i++;
  }
  return candidate;
}

function FieldLibrary({ onAdd }: { onAdd: (type: FormFieldType) => void }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-neutral-200 bg-neutral-50 p-3">
        <h3 className="text-sm font-semibold text-neutral-700">Tipos de campo</h3>
      </div>
      <div className="grid grid-cols-2 gap-1 p-2 lg:grid-cols-1">
        {FIELD_LIBRARY.map((t) => (
          <button
            key={t.type}
            type="button"
            onClick={() => onAdd(t.type)}
            className="flex items-center gap-2 rounded-md px-2 py-2 text-left text-xs font-medium text-neutral-700 hover:bg-neutral-100"
          >
            <span className="grid size-7 place-items-center rounded bg-neutral-100 text-neutral-600">
              {t.icon}
            </span>
            {t.label}
          </button>
        ))}
      </div>
    </Card>
  );
}

function SortableFieldRow({
  field, isSelected, onSelect, onRemove, onDuplicate,
}: {
  field: FormFieldData;
  isSelected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const meta = FIELD_LIBRARY.find((t) => t.type === field.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={cn(
        'group flex items-center gap-2 rounded-lg border bg-white p-2 transition-colors',
        isSelected ? 'border-neutral-900 ring-2 ring-neutral-900/10' : 'border-neutral-200 hover:border-neutral-300',
        isDragging && 'opacity-50',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab text-neutral-300 hover:text-neutral-500 active:cursor-grabbing"
        aria-label="Reordenar"
      >
        <GripVertical className="size-4" />
      </button>
      <span className="grid size-7 place-items-center rounded bg-neutral-100 text-neutral-500">
        {meta?.icon ?? <Type className="size-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-neutral-900">
          {field.label || <span className="text-neutral-400">Sin etiqueta</span>}
        </div>
        <div className="flex items-center gap-1.5 truncate text-xs text-neutral-500">
          <code className="rounded bg-neutral-100 px-1 py-0.5 text-[10px]">{field.name}</code>
          <span>·</span>
          <span>{meta?.label ?? field.type}</span>
          {field.required && <Badge variant="outline" className="ml-1 h-4 px-1 text-[9px]">req</Badge>}
        </div>
      </div>
      <div className="opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
          className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          aria-label="Duplicar"
        >
          <CopyIcon className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
          aria-label="Eliminar"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

function FieldInspector({
  field, onChange, onRemove,
}: {
  field: FormFieldData | null;
  onChange: (patch: Partial<FormFieldData>) => void;
  onRemove: () => void;
}) {
  if (!field) {
    return (
      <Card className="p-4 text-sm text-neutral-400">
        <div className="flex items-center gap-2 text-neutral-500">
          <Settings className="size-4" /> Inspector
        </div>
        <p className="mt-2 text-xs">Selecciona un campo para editar sus propiedades.</p>
      </Card>
    );
  }

  const supportsOptions = field.type === 'select' || field.type === 'radio';
  const supportsPattern = ['text', 'email', 'tel', 'phone', 'url'].includes(field.type);
  const supportsMinMax = ['number', 'text', 'textarea'].includes(field.type);
  const supportsDigits = field.type === 'tel' || field.type === 'phone';

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-neutral-200 bg-neutral-50 p-3">
        <h3 className="text-sm font-semibold text-neutral-700">Propiedades del campo</h3>
        <p className="text-xs text-neutral-500">{field.type}</p>
      </div>
      <div className="space-y-3 p-3">
        <div className="space-y-1">
          <Label>Etiqueta</Label>
          <Input
            value={field.label}
            onChange={(e) => onChange({ label: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label>Nombre (clave técnica)</Label>
          <Input
            value={field.name}
            onChange={(e) => onChange({ name: slug(e.target.value) })}
          />
          <p className="text-[10px] text-neutral-400">Letras, números y _. Sin espacios.</p>
        </div>
        {field.type !== 'hidden' && field.type !== 'checkbox' && (
          <div className="space-y-1">
            <Label>Placeholder</Label>
            <Input
              value={field.placeholder ?? ''}
              onChange={(e) => onChange({ placeholder: e.target.value })}
            />
          </div>
        )}
        <div className="space-y-1">
          <Label>Texto de ayuda</Label>
          <Textarea
            rows={2}
            value={field.helper ?? ''}
            onChange={(e) => onChange({ helper: e.target.value })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor={`req-${field.id}`}>Obligatorio</Label>
          <input
            id={`req-${field.id}`}
            type="checkbox"
            checked={!!field.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            className="size-4 accent-neutral-900"
          />
        </div>
        <div className="space-y-1">
          <Label>Ancho</Label>
          <select
            value={field.width ?? 'full'}
            onChange={(e) => onChange({ width: e.target.value as FormFieldData['width'] })}
            className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
          >
            <option value="full">Completo</option>
            <option value="two-thirds">2/3</option>
            <option value="half">Mitad</option>
            <option value="third">1/3</option>
          </select>
        </div>

        {supportsOptions && (
          <OptionsEditor
            options={field.options ?? []}
            onChange={(options) => onChange({ options })}
          />
        )}

        {supportsDigits && (
          <details className="rounded-md border border-neutral-200 p-2" open>
            <summary className="cursor-pointer text-xs font-semibold text-neutral-600">
              Dígitos del teléfono
            </summary>
            <div className="mt-2 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Mín. dígitos</Label>
                  <Input
                    type="number"
                    min={0}
                    value={field.validation?.min ?? ''}
                    onChange={(e) => onChange({
                      validation: { ...field.validation, min: e.target.value === '' ? undefined : Number(e.target.value) },
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Máx. dígitos</Label>
                  <Input
                    type="number"
                    min={0}
                    value={field.validation?.max ?? ''}
                    onChange={(e) => onChange({
                      validation: { ...field.validation, max: e.target.value === '' ? undefined : Number(e.target.value) },
                    })}
                  />
                </div>
              </div>
              <p className="text-[10px] text-neutral-400">
                Cuenta solo los dígitos (ignora espacios y el prefijo +). Para exigir un número exacto, pon el mismo valor en mín y máx (p. ej. 9 y 9).
              </p>
            </div>
          </details>
        )}

        {(supportsMinMax || supportsPattern) && (
          <details className="rounded-md border border-neutral-200 p-2">
            <summary className="cursor-pointer text-xs font-semibold text-neutral-600">
              Validación avanzada
            </summary>
            <div className="mt-2 space-y-2">
              {supportsMinMax && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Min</Label>
                    <Input
                      type="number"
                      value={field.validation?.min ?? ''}
                      onChange={(e) => onChange({
                        validation: { ...field.validation, min: e.target.value === '' ? undefined : Number(e.target.value) },
                      })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Max</Label>
                    <Input
                      type="number"
                      value={field.validation?.max ?? ''}
                      onChange={(e) => onChange({
                        validation: { ...field.validation, max: e.target.value === '' ? undefined : Number(e.target.value) },
                      })}
                    />
                  </div>
                </div>
              )}
              {supportsPattern && (
                <div className="space-y-1">
                  <Label className="text-xs">Pattern (RegExp)</Label>
                  <Input
                    value={field.validation?.pattern ?? ''}
                    onChange={(e) => onChange({
                      validation: { ...field.validation, pattern: e.target.value },
                    })}
                    placeholder="^[A-Z]+$"
                  />
                </div>
              )}
            </div>
          </details>
        )}

        <button
          type="button"
          onClick={onRemove}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-red-200 px-2 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
        >
          <Trash2 className="size-3.5" /> Eliminar campo
        </button>
      </div>
    </Card>
  );
}

function OptionsEditor({
  options, onChange,
}: { options: FormFieldOption[]; onChange: (options: FormFieldOption[]) => void }) {
  return (
    <div className="space-y-2">
      <Label>Opciones</Label>
      <div className="space-y-1">
        {options.map((opt, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1">
            <Input
              placeholder="valor"
              value={opt.value}
              onChange={(e) => {
                const next = [...options];
                next[i] = { ...opt, value: e.target.value };
                onChange(next);
              }}
            />
            <Input
              placeholder="Etiqueta visible"
              value={opt.label}
              onChange={(e) => {
                const next = [...options];
                next[i] = { ...opt, label: e.target.value };
                onChange(next);
              }}
            />
            <button
              type="button"
              onClick={() => onChange(options.filter((_, idx) => idx !== i))}
              className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Eliminar opción"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => onChange([...options, { value: '', label: '' }])}
      >
        <Plus className="size-3.5" /> Añadir opción
      </Button>
    </div>
  );
}
